import React from "react";
import { CalendarDaysIcon, MapPinIcon, TagIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import TapAndPlayIcon from "@mui/icons-material/TapAndPlay";

function formatDetailDate(value) {
    if (!value) return null;

    const date = new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
}

function formatCoordinate(value) {
    if (value == null) return null;

    const number = Number(value);

    return Number.isFinite(number) ? number.toFixed(5) : value;
}

function DetailValue({ label, children, className = "" }) {
    return (
        <div className={`min-w-0 ${className}`}>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
            <div className="mt-1 break-words text-[15px] font-medium text-gray-700">{children}</div>
        </div>
    );
}

function SectionTitle({ icon: Icon, iconClassName = "", children }) {
    return (
        <div className="mb-3 flex items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
                <Icon className={iconClassName || "h-4 w-4"} />
            </div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{children}</h4>
        </div>
    );
}

export default function WorkingAreaDetailRow({ area }) {
    const justification = typeof area?.justification === "object" ? area.justification : null;
    const nfcTag = typeof area?.nfc_tag === "object" ? area.nfc_tag : null;

    const hasCoordinates = area?.latitude != null && area?.longitude != null;
    const validity = !area?.valid_from && !area?.valid_to
        ? "Sempre attiva"
        : `${formatDetailDate(area?.valid_from) || "…"} → ${formatDetailDate(area?.valid_to) || "…"}`;

    return (
        <div className="overflow-hidden rounded-lg border border-indigo-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 bg-indigo-50/50 px-4 py-3">
                <div>
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-indigo-800">
                        {area?.color && (
                            <span
                                className="inline-block h-3.5 w-3.5 shrink-0 rounded-[3px] ring-1 ring-black/10"
                                style={{backgroundColor: area.color}}
                            />
                        )}

                        {area?.name || "Area di lavoro"}
                    </h3>

                    <p className="mt-0.5 text-xs text-gray-500">
                        Dettagli e configurazione dell’area
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 divide-y divide-gray-300 lg:grid-cols-[30%_40%_30%] lg:divide-x lg:divide-y-0">
                <section className="p-4">
                    <SectionTitle icon={TagIcon}>Configurazione</SectionTitle>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                        <DetailValue label="Identificativo area">
                            {area?.justification_code ? (
                                <span>{area?.justification_code}{' - '}{area?.justification_description}</span>
                            ) : (<span className="font-normal text-gray-400">Non associato</span>)}
                            
                        </DetailValue>

                        <DetailValue label="Tag NFC">
                            {nfcTag ? (
                                <span className="inline-flex items-center gap-1.5 text-indigo-700">
                                    <TapAndPlayIcon className="!h-4 !w-4" />
                                    {nfcTag.name || "Senza nome"}
                                </span>
                            ) : (
                                <span className="font-normal text-gray-400">Non associato</span>
                            )}
                        </DetailValue>
                    </div>
                </section>

                <section className="p-4">
                    <SectionTitle icon={MapPinIcon}>Posizione</SectionTitle>

                    <div className="space-y-4">
                        <DetailValue label="Indirizzo">
                            {area?.address || <span className="font-normal text-gray-400">Non impostato</span>}
                        </DetailValue>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <DetailValue label="Coordinate">
                                {hasCoordinates
                                    ? `${formatCoordinate(area.latitude)}, ${formatCoordinate(area.longitude)}`
                                    : <span className="font-normal text-gray-400">Non impostate</span>}
                            </DetailValue>

                            <DetailValue label="Raggio">
                                {area?.radius != null
                                    ? `${area.radius} m`
                                    : <span className="font-normal text-gray-400">Non impostato</span>}
                            </DetailValue>
                        </div>
                    </div>
                </section>

                <section className="p-4">
                    <SectionTitle icon={CalendarDaysIcon}>Validità</SectionTitle>

                    <div className="space-y-4">
                        <DetailValue label="Periodo">{validity}</DetailValue>

                        <div className="grid grid-cols-2 gap-4">
                            <DetailValue label="Dal">
                                {formatDetailDate(area?.valid_from) || <span className="font-normal text-gray-400">Non impostata</span>}
                            </DetailValue>

                            <DetailValue label="Al">
                                {formatDetailDate(area?.valid_to) || <span className="font-normal text-gray-400">Non impostata</span>}
                            </DetailValue>
                        </div>
                    </div>
                </section>
            </div>

            {(area?.notification_timing || area?.notification_email) && (
                <section className="border-t border-gray-300 bg-white px-4 py-4">
                    <SectionTitle icon={CalendarDaysIcon}>
                        Promemoria scadenza
                    </SectionTitle>

                    <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                        <DetailValue label="Notifica">
                            {area?.notification_timing ? (
                                <span>
                                    {area.notification_timing}{" "}
                                    {Number(area.notification_timing) === 1
                                        ? "giorno prima della scadenza"
                                        : "giorni prima della scadenza"}
                                </span>
                            ) : (
                                <span className="font-normal text-gray-400">
                                    Non impostata
                                </span>
                            )}
                        </DetailValue>

                        <DetailValue label="Destinatari email">
                            {area?.notification_email ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {area.notification_email.split(",").map((email) => email.trim()).filter(Boolean).map((email) => (
                                        <span key={email} className="inline-flex rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                            {email}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="font-normal text-gray-400">Non impostati</span>
                            )}
                        </DetailValue>
                    </div>
                </section>
            )}

            <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
                        <DocumentTextIcon className="h-4 w-4" />
                    </div>

                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Note</p>
                        <p className={`mt-1 whitespace-pre-wrap text-[13px] leading-5 ${area?.notes ? "text-gray-700" : "text-gray-400"}`}>
                            {area?.notes || "Nessuna nota inserita"}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
